import { useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import StatusBadge from "../StatusBadge";
import { fmtSEK } from "../planning/revenue/format";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

export const CLASSIFICATIONS = [
  { value: "REVENUE", label: "Revenue" },
  { value: "SUBCONTRACTOR_DIRECT_COST", label: "Subcontractor direct cost" },
  { value: "PERSONNEL_COST", label: "Personnel cost" },
  { value: "REGULAR_INDIRECT_COST", label: "Regular indirect cost" },
  { value: "GROUP_OVERHEAD", label: "Group overhead" },
  { value: "FACTORING", label: "Factoring" },
  { value: "DEPRECIATION", label: "Depreciation" },
  { value: "INTEREST", label: "Interest" },
  { value: "OTHER_UNCLASSIFIED", label: "Other / Unclassified" },
];
export const CLASSIFICATION_LABELS = Object.fromEntries(CLASSIFICATIONS.map((c) => [c.value, c.label]));

export const SALES_LINES = [
  { value: "tm", label: "Time & Material" },
  { value: "projects", label: "Projects" },
  { value: "services", label: "Services" },
  { value: "other", label: "Other revenue" },
];

export const STATUS_TONES = { IMPORTED: "amber", VERIFIED: "blue", COMMITTED: "green", SUPERSEDED: "slate" };

const MAPPING_TONES = { MAPPED: "green", MATCHED: "blue", SUGGESTED: "amber", UNMAPPED: "red" };

const COMPOSITION_ROWS = [
  ["tm", "Revenue — Time & Material"],
  ["projects", "Revenue — Projects"],
  ["services", "Revenue — Services"],
  ["other", "Revenue — Other"],
  ["factoring", "Factoring"],
  ["subcontractor_direct", "Subcontractor direct cost"],
  ["personnel_cost", "Personnel cost"],
  ["regular_indirect", "Regular indirect cost"],
  ["group_overhead", "Group overhead"],
  ["depreciation", "Depreciation"],
  ["interest", "Interest"],
  ["other_unclassified", "Other / Unclassified"],
];

function detailError(e) {
  const d = e?.response?.data?.detail;
  if (d && typeof d === "object" && d.reasons) return `${d.message}: ${d.reasons.join(" ")}`;
  return apiError(e);
}

export default function BatchDetail({ batch, onUpdated, onDeleted, canWrite }) {
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState("");
  const recon = batch.reconciliation || {};
  const readiness = batch.readiness;
  const imported = batch.status === "IMPORTED";
  const verified = batch.status === "VERIFIED";

  const act = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      toast.success(successMsg);
      onUpdated(res.data);
    } catch (e) {
      toast.error(detailError(e));
    } finally {
      setBusy("");
    }
  };

  const saveRow = (row) =>
    act(`row-${row.id}`, async () => {
      const edit = edits[row.id] || {};
      const res = await api.put(`/actuals/batches/${batch.id}/rows/${row.id}/map`, {
        classification: edit.classification || row.classification,
        sales_line: (edit.sales_line ?? row.sales_line) || null,
        cost_account_id: row.cost_account_id,
      });
      setEdits((prev) => ({ ...prev, [row.id]: undefined }));
      return res;
    }, "Mapping saved — reused by future imports");

  const deleteBatch = async () => {
    setBusy("delete");
    try {
      await api.delete(`/actuals/batches/${batch.id}`);
      toast.success("Draft import deleted");
      onDeleted();
    } catch (e) {
      toast.error(detailError(e));
    } finally {
      setBusy("");
    }
  };

  const reconRow = (label, computed, reported, delta, testId) => (
    <tr className="border-b border-slate-100" data-testid={testId}>
      <td className="py-1.5 px-3 text-sm text-slate-700">{label}</td>
      <td className="py-1.5 px-3 text-sm text-right text-slate-900">{computed == null ? "—" : `${fmtSEK(computed)} kr`}</td>
      <td className="py-1.5 px-3 text-sm text-right text-slate-500">{reported == null ? "—" : `${fmtSEK(reported)} kr`}</td>
      <td className={`py-1.5 px-3 text-sm text-right font-semibold ${delta === 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`} data-testid={`${testId}-delta`}>
        {delta == null ? "—" : fmtSEK(delta)}
      </td>
    </tr>
  );

  return (
    <div className="space-y-5" data-testid="batch-detail">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-900" data-testid="batch-file-name">{batch.file_name}</h2>
              <StatusBadge tone={STATUS_TONES[batch.status]} testId="batch-status-badge">{batch.status}</StatusBadge>
            </div>
            <p className="text-xs text-slate-500 mt-1" data-testid="batch-meta">
              {batch.parsed.company_name || "Unknown company"} · {batch.parsed.organization_number || "no org no."} ·
              Report period {batch.parsed.period_start} – {batch.parsed.period_end} · Fiscal year {batch.parsed.fiscal_year_start} – {batch.parsed.fiscal_year_end}
            </p>
            <p className="text-xs text-slate-400 mt-0.5">
              Imported {new Date(batch.created_at).toLocaleString("sv-SE")}
              {batch.verified_at && ` · Verified ${new Date(batch.verified_at).toLocaleString("sv-SE")}`}
              {batch.committed_at && ` · Committed ${new Date(batch.committed_at).toLocaleString("sv-SE")}`}
            </p>
            {batch.status === "SUPERSEDED" && (
              <p className="text-xs text-slate-500 mt-1" data-testid="batch-superseded-info">
                Superseded {new Date(batch.superseded_at).toLocaleString("sv-SE")} — reason: {batch.superseded_comment}
              </p>
            )}
            {batch.replacement_comment && (
              <p className="text-xs text-slate-500 mt-1" data-testid="batch-replacement-comment">
                Replacement reason: {batch.replacement_comment}
              </p>
            )}
          </div>
          {canWrite && (
            <div className="flex items-center gap-2" data-testid="batch-actions">
              {imported && batch.counts.suggested > 0 && (
                <Button variant="outline" size="sm" disabled={!!busy}
                  onClick={() => act("accept", () => api.post(`/actuals/batches/${batch.id}/accept-suggestions`), "Suggestions confirmed")}
                  data-testid="batch-accept-suggestions">
                  {busy === "accept" && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                  Confirm {batch.counts.suggested} suggestions
                </Button>
              )}
              {imported && (
                <Button size="sm" className="bg-[#0052CC] hover:bg-[#0047B3]" disabled={!!busy || (readiness && !readiness.ready)}
                  onClick={() => act("verify", () => api.post(`/actuals/batches/${batch.id}/verify`), "Batch verified")}
                  data-testid="batch-verify-button">
                  {busy === "verify" && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                  Verify
                </Button>
              )}
              {verified && (
                <Button size="sm" className="bg-[#15803D] hover:bg-[#166534]" disabled={!!busy}
                  onClick={() => act("commit", () => api.post(`/actuals/batches/${batch.id}/commit`), "Actuals committed — authoritative in P&L")}
                  data-testid="batch-commit-button">
                  {busy === "commit" && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                  Commit actuals
                </Button>
              )}
              {imported && (
                <Button variant="outline" size="sm" className="text-[#B91C1C]" disabled={!!busy} onClick={deleteBatch} data-testid="batch-delete-button">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>

        {batch.validation_warnings?.length > 0 && (
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2" data-testid="batch-warnings">
            <AlertTriangle className="h-4 w-4 text-[#B45309] shrink-0 mt-0.5" />
            <div className="text-xs text-slate-700 space-y-0.5">
              {batch.validation_warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          </div>
        )}
        {readiness && !readiness.ready && (
          <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="batch-readiness-blockers">
            <div className="text-xs font-semibold text-[#B91C1C] mb-1">Verification blocked:</div>
            <div className="text-xs text-slate-700 space-y-0.5">
              {readiness.reasons.map((r, i) => <div key={i}>{r}</div>)}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="batch-reconciliation">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Reconciliation to reported totals</h3>
            {recon.reconciled ? (
              <StatusBadge tone="green" testId="recon-status"><CheckCircle2 className="h-3 w-3 mr-1 inline" />Reconciled</StatusBadge>
            ) : (
              <StatusBadge tone="red" testId="recon-status">Out of balance</StatusBadge>
            )}
          </div>
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200">
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Measure</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Parsed (Perioden)</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Reported</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Delta</th>
              </tr>
            </thead>
            <tbody>
              {reconRow("Revenue", recon.computed_revenue, recon.reported_revenue, recon.delta_revenue, "recon-revenue")}
              {reconRow("Costs", recon.computed_cost, recon.reported_cost, recon.delta_cost, "recon-cost")}
              {reconRow("Profit before tax", recon.computed_pbt, recon.reported_pbt, recon.delta_pbt, "recon-pbt")}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="batch-composition">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-900">P&L composition (P&L convention — costs positive)</h3>
          </div>
          <table className="w-full">
            <tbody>
              {COMPOSITION_ROWS.filter(([k]) => batch.composition?.[k]).map(([k, label]) => (
                <tr key={k} className="border-b border-slate-100" data-testid={`comp-${k}`}>
                  <td className="py-1.5 px-3 text-sm text-slate-700">{label}</td>
                  <td className="py-1.5 px-3 text-sm text-right font-medium text-slate-900">{fmtSEK(batch.composition[k])} kr</td>
                </tr>
              ))}
              {!batch.composition && (
                <tr><td className="py-4 px-3 text-sm text-slate-400">Composition is computed at verification.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="batch-rows">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-900">Account postings — {batch.counts.rows} rows</h3>
          <span className="text-xs text-slate-500" data-testid="batch-mapping-summary">
            {batch.counts.unmapped} unmapped · {batch.counts.suggested} suggestions awaiting confirmation
          </span>
        </div>
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200 sticky top-0">
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Account</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Description</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Perioden</th>
                <th className="text-right text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Ackumulerat</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Mapping</th>
                <th className="text-left text-[11px] font-semibold text-slate-500 uppercase py-2 px-3">Classification</th>
                <th className="py-2 px-3" />
              </tr>
            </thead>
            <tbody>
              {batch.rows.map((r) => {
                const edit = edits[r.id] || {};
                const classification = edit.classification ?? r.classification;
                const salesLine = edit.sales_line ?? r.sales_line;
                const dirty = !!edits[r.id];
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-accent/40" data-testid={`batch-row-${r.account_number}`}>
                    <td className="py-1.5 px-3 text-sm font-medium text-slate-900">{r.account_number}</td>
                    <td className="py-1.5 px-3 text-sm text-slate-600 max-w-64 truncate" title={r.description}>{r.description}</td>
                    <td className={`py-1.5 px-3 text-sm text-right ${r.period_amount < 0 ? "text-[#B91C1C]" : "text-slate-900"}`}>{fmtSEK(r.period_amount)}</td>
                    <td className="py-1.5 px-3 text-xs text-right text-slate-400">{r.accumulated_amount == null ? "—" : fmtSEK(r.accumulated_amount)}</td>
                    <td className="py-1.5 px-3">
                      <StatusBadge tone={MAPPING_TONES[r.mapping_status]} testId={`batch-row-status-${r.account_number}`}>
                        {r.mapping_status}{r.account_exists ? "" : " · new account"}
                      </StatusBadge>
                    </td>
                    <td className="py-1.5 px-3">
                      {imported && canWrite ? (
                        <div className="flex items-center gap-1.5">
                          <Select value={classification || ""} onValueChange={(v) => setEdits((p) => ({ ...p, [r.id]: { ...p[r.id], classification: v } }))}>
                            <SelectTrigger className="h-7 w-52 text-xs" data-testid={`batch-row-class-${r.account_number}`}>
                              <SelectValue placeholder="Unmapped" />
                            </SelectTrigger>
                            <SelectContent>
                              {CLASSIFICATIONS.map((c) => (
                                <SelectItem key={c.value} value={c.value} data-testid={`batch-row-class-${r.account_number}-${c.value}`}>{c.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {classification === "REVENUE" && (
                            <Select value={salesLine || "other"} onValueChange={(v) => setEdits((p) => ({ ...p, [r.id]: { ...p[r.id], sales_line: v } }))}>
                              <SelectTrigger className="h-7 w-40 text-xs" data-testid={`batch-row-salesline-${r.account_number}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SALES_LINES.map((s) => (
                                  <SelectItem key={s.value} value={s.value} data-testid={`batch-row-salesline-${r.account_number}-${s.value}`}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-700">
                          {CLASSIFICATION_LABELS[r.classification] || "—"}
                          {r.sales_line ? ` · ${SALES_LINES.find((s) => s.value === r.sales_line)?.label}` : ""}
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 px-3 text-right">
                      {imported && canWrite && dirty && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!!busy} onClick={() => saveRow(r)} data-testid={`batch-row-save-${r.account_number}`}>
                          {busy === `row-${r.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : "Save"}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
